import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BranchTargetPopup } from "../components/BranchTargetPopup";
import { DeployGuardBanner } from "../components/DeployGuardBanner";
import { PendingUsageCard } from "../components/aset/PendingUsageCard";
import { Activity, Banknote, Boxes, Building2, Crown, FileText, Moon, Sun, Headset, MessageSquareWarning, Network, Package, Plus, RefreshCcw, ScanSearch, Server, Settings2, ShoppingCart, Ticket, Trash2, Unlink, Users, Wifi } from "lucide-react";
import { TileEditor, applyTilePrefs, tileKey } from "../components/TileEditor";
import { api, errMsg, fmtDateTime, rupiah } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useRouters } from "../context/RouterContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";

const fmtBytes = (b) => {
  const n = Number(b || 0);
  if (n >= 1073741824) return `${(n / 1073741824).toFixed(2)} GB`;
  if (n >= 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  return `${(n / 1024).toFixed(0)} KB`;
};
const fmtBps = (b) => (b >= 1e6 ? `${(b / 1e6).toFixed(1)} Mbps` : `${(b / 1e3).toFixed(0)} kbps`);
const FUP_LIMIT_BYTES = 4000 * 1024 ** 3; // batas FUP 4.000 GB
const BOARD_MODEL = { "hEX PoE": "RB960PGS", "hEX": "RB750Gr3", "hEX S": "RB760iGS", "hEX lite": "RB750r2", "hEX PoE lite": "RB750UPr2", "hAP ac2": "RBD52G", "hAP ac3": "RBD53iG", "hAP ax2": "C52iG", "hAP ax3": "C53UiG", "hAP lite": "RB941-2nD", "RB4011iGS+": "RB4011", "CCR1009-7G-1C-1S+": "CCR1009", "CCR1016-12G": "CCR1016", "CCR2004-16G-2S+": "CCR2004", "RB1100AHx4": "RB1100AHx4", "RB2011UiAS-2HnD": "RB2011", "RB3011UiAS": "RB3011", "L009UiGS": "L009", "RB5009UG+S+": "RB5009" };
const boardLabel = (b) => (b ? (BOARD_MODEL[b] ? `${b} · ${BOARD_MODEL[b]}` : b) : "-");
const fmtUptimeShort = (u) => { const m = String(u || "").match(/(?:(\d+)w)?(?:(\d+)d)?(?:(\d+)h)?(?:(\d+)m)?/); if (!m) return "-"; const d = (+m[1] || 0) * 7 + (+m[2] || 0), h = +m[3] || 0, mn = +m[4] || 0; return [d && `${d} hari`, (d || h) && `${h} jam`, `${mn} mnt`].filter(Boolean).join(" "); };

const TILES = [
  { to: "/vouchers?add-voucher=1", path: "/vouchers", icon: Ticket, label: "Buat Voucher", bg: "bg-[#F04E23]", plus: true },
  { to: "/vouchers", icon: FileText, label: "Data Voucher", bg: "bg-teal-600" },
  { to: "/ppp", icon: Network, label: "WiFi Rumahan", bg: "bg-rose-500" },
  { to: "/reset-voucher", icon: Unlink, label: "Reset Voucher", bg: "bg-amber-500" },
  { to: "/agen", icon: Users, label: "Agen & Komisi", bg: "bg-emerald-600" },
  { to: "/routers", icon: Server, label: "Cabang", bg: "bg-sky-600", adminOnly: true },
  { to: "/komplain-wifi", icon: Headset, label: "Chat Admin", bg: "bg-indigo-600" },
  { to: "/komplain-ppp", icon: MessageSquareWarning, label: "Komplain Saya", bg: "bg-fuchsia-600", agentOnly: true },
  { to: "/isi-saldo", icon: Banknote, label: "Isi Saldo", bg: "bg-orange-500" },
  { to: "/voucher-tuan-rumah", icon: Crown, label: "Voucher Tuan Rumah", bg: "bg-yellow-500" },
  { to: "/aset", icon: Boxes, label: "Aset", bg: "bg-cyan-600" },
  { to: "/lacak-aset", icon: ScanSearch, label: "Lacak Aset", bg: "bg-lime-600" },
  { to: "/stok", icon: ShoppingCart, label: "Stok", bg: "bg-violet-600", adminOnly: true },
  { to: "/pesan-alat", icon: Package, label: "Order Alat", bg: "bg-pink-600" },
  { to: "/buka-cabang", icon: Building2, label: "Buka Cabang Baru", bg: "bg-sky-600", needsBranch: true },
  { to: "/buka-cabang", path: "/buka-cabang-admin", icon: Building2, label: "Pengajuan Cabang Baru", bg: "bg-blue-600", adminOnly: true },
];

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const { selected, router, routers } = useRouters();
  const isAdmin = ["admin", "admin_fasnet"].includes(user?.role);
  // daftar router utk non-admin sudah difilter backend hanya yg tertaut → hitung panjangnya saja
  const isAdminRole = ["admin", "admin_fasnet"].includes(user?.role);
  const [stats, setStats] = useState(null);
  const [telemetry, setTelemetry] = useState(null);
  const [traffic, setTraffic] = useState([]);
  const [targetOpen, setTargetOpen] = useState(false);
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  const toggleTheme = () => { const next = !dark; setDark(next); window.dispatchEvent(new CustomEvent("fasnet-theme", { detail: next ? "dark" : "light" })); };
  const [teleChecked, setTeleChecked] = useState(false);
  const [editTiles, setEditTiles] = useState(false);
  const allowedTiles = TILES.filter((t) => (!t.adminOnly || isAdmin) && (!t.rolesOnly || t.rolesOnly.includes(user?.role)) && (!t.agentOnly || !isAdmin) && (!t.needsBranch || user?.can_open_branch) && !(user?.hidden_menus || []).includes(t.path || t.to));
  const visibleTiles = applyTilePrefs(allowedTiles, user?.tile_prefs).filter((t) => !(user?.tile_prefs?.hidden || []).includes(tileKey(t)));
  const ifaceRef = useRef("");

  const loadStats = useCallback(() => {
    return api.get("/dashboard/stats", { params: { router_id: selected || undefined } })
      .then((r) => setStats(r.data))
      .catch(() => setStats(null));
  }, [selected]);

  const loadTele = useCallback(() => {
    if (!selected) return Promise.resolve();
    return api.get(`/routers/${selected}/telemetry`).then((r) => {
      setTelemetry(r.data);
      if (r.data.traffic_iface) ifaceRef.current = r.data.traffic_iface;
    }).catch(() => setTelemetry(null)).finally(() => setTeleChecked(true));
  }, [selected]);

  const loadTraffic = useCallback(() => {
    if (!selected || !ifaceRef.current) return Promise.resolve();
    return api.get(`/routers/${selected}/traffic`, { params: { interface: ifaceRef.current } })
      .then((r) => setTraffic((p) => [...p.slice(-23), r.data]))
      .catch(() => {});
  }, [selected]);

  const [refreshing, setRefreshing] = useState(false);
  const refreshAll = async () => {
    setRefreshing(true);
    const started = Date.now();
    try {
      if (isAdmin && selected) {
        try {
          await api.post(`/routers/${selected}/test`);
          toast.success("Koneksi ke MikroTik berhasil — router online kembali");
        } catch (err) {
          toast.error(`Koneksi ke MikroTik gagal: ${errMsg(err)}`);
        }
      }
      await Promise.all([loadStats(), loadTele(), loadTraffic()]);
      // Pastikan spinner terlihat minimal ~1 detik agar pengguna tahu sistem merespon
      const elapsed = Date.now() - started;
      if (elapsed < 1000) await new Promise((r) => setTimeout(r, 1000 - elapsed));
    } finally { setRefreshing(false); }
  };

  useEffect(() => {
    loadStats();
    const i = setInterval(loadStats, 30000);
    return () => clearInterval(i);
  }, [loadStats]);

  useEffect(() => {
    if (!selected) { setTelemetry(null); setTraffic([]); setTeleChecked(false); return undefined; }
    setTraffic([]);
    setTeleChecked(false);
    ifaceRef.current = "";
    loadTele();
    const i1 = setInterval(loadTele, 5000);
    const i2 = setInterval(loadTraffic, 4000);
    const t0 = setTimeout(loadTraffic, 1500);
    return () => { clearInterval(i1); clearInterval(i2); clearTimeout(t0); };
  }, [selected, loadTele, loadTraffic]);

  const s = stats || {};
  return (
    <div className="space-y-5" data-testid="dashboard-page">
      <style>{`@keyframes autoRefreshBar { from { width: 0%; } to { width: 100%; } } .auto-refresh-bar { animation: autoRefreshBar 5s linear infinite; }`}</style>
      {isAdmin && <DeployGuardBanner />}
      {/* Hero ala aplikasi PPOB — Dark Navy brand fasnetfiber.com */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#050914] via-[#0A1226] to-[#040810] text-white p-5 sm:p-7 shadow-xl shadow-black/25 border border-[#1A2644]" data-testid="hero-card">
        <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#F04E23]/15 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="mb-3" data-testid="hero-agent-identity">
              <div className="font-heading font-bold text-base sm:text-lg" data-testid="hero-agent-name">{user?.name || "Admin"}</div>
              <div className="text-xs text-white/60 font-mono" data-testid="hero-agent-meta">{[user?.agent_code, isAdminRole ? `${routers.length} cabang terdaftar` : `${routers.length} cabang tertaut`].filter(Boolean).join(" • ")}</div>
            </div>
            <Link
              to={isAdmin ? "/saldo" : "/isi-saldo"}
              className="inline-flex items-center px-3 py-1.5 rounded-xl bg-white/15 border border-white/30 text-sm sm:text-base font-mono font-bold text-amber-300 hover:bg-white/25 active:scale-95 transition-all shadow-sm mb-2.5"
              title="Lihat Mutasi Saldo"
              data-testid="hero-saldo"
            >
              {isAdmin ? "∞" : rupiah(user?.balance || 0)}
            </Link>
            <div className="text-white/75 text-sm">Router Aktif</div>
            <Link to="/routerboard" className="inline-block group" title="Klik untuk buka Routerboard" data-testid="hero-router-link">
              <div className="font-heading font-bold text-lg sm:text-xl group-hover:underline underline-offset-4 rainbow-text" data-testid="hero-router-name">{router ? router.name : "FASNET WIFI"}</div>
              <div className="text-[10px] text-white/60 font-mono">Klik Routerboard</div>
            </Link>
          </div>
          <div className="flex flex-col items-end gap-2" data-testid="router-status-badge">
            {!teleChecked ? (
              <span className="inline-flex flex-col items-end gap-1.5" data-testid="hero-status-checking">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono bg-white/15 text-white border border-white/20">Mengecek router...</span>
                <span className="w-28 h-1 rounded-full bg-white/20 overflow-hidden block">
                  <span className="h-full bg-amber-300 auto-refresh-bar block" />
                </span>
              </span>
            ) : telemetry ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono bg-emerald-400/20 text-emerald-100 border border-emerald-300/30">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" /> Online
              </span>
            ) : (
              <>
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono bg-white/15 text-white border border-white/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-300" /> Router Offline
                </span>
                <button onClick={refreshAll} disabled={refreshing} data-testid="btn-refresh-hero" className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-mono bg-white/15 text-white border border-white/20 hover:bg-white/25 transition-colors disabled:opacity-60">
                  <RefreshCcw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "MEMUAT..." : "REFRESH"}
                </button>
              </>
            )}
            <button type="button" onClick={toggleTheme} data-testid="btn-theme-hero" title={dark ? "Ganti ke tampilan terang" : "Ganti ke tampilan gelap"} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono bg-white/15 text-white border border-white/20 hover:bg-white/25 transition-colors">
              {dark ? <Sun className="w-3.5 h-3.5 text-amber-300" /> : <Moon className="w-3.5 h-3.5 text-sky-200" />} {dark ? "Mode Terang" : "Mode Gelap"}
            </button>
          </div>
        </div>
      </div>

      {/* Grid menu ala PPOB */}
      <BranchTargetPopup open={targetOpen} onClose={() => setTargetOpen(false)} />
      {editTiles ? (
        <TileEditor tiles={allowedTiles} prefs={user?.tile_prefs} onClose={() => setEditTiles(false)} onSaved={async () => { await refreshUser(); setEditTiles(false); }} />
      ) : (<>
      <div className="flex justify-end -mb-1">
        <button type="button" onClick={() => setEditTiles(true)} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" data-testid="btn-edit-tiles"><Settings2 className="w-3.5 h-3.5" /> Atur menu</button>
      </div>
      <div className={`grid ${isAdmin ? "grid-cols-4" : "grid-cols-3"} gap-3`} data-testid="menu-tiles">
        {visibleTiles.map((t) => (
          <Link key={t.label} to={t.to} onClick={(e) => { if (t.needsBranch) { e.preventDefault(); setTargetOpen(true); } }} data-testid={`quick-${(t.path || t.to).replace(/\//g, "-").slice(1)}${t.plus ? "-add" : ""}`} className="flex flex-col items-center gap-2 group">
            <div className={`relative w-12 h-12 sm:w-14 sm:h-14 rounded-2xl ${t.bg} text-white flex items-center justify-center shadow-md transition-transform duration-200 group-hover:scale-105 group-active:scale-95`}>
              <t.icon className="w-6 h-6" />
              {t.plus && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white text-[#F04E23] flex items-center justify-center shadow border border-orange-100" data-testid="badge-plus-voucher">
                  <Plus className="w-3.5 h-3.5" strokeWidth={3} />
                </span>
              )}
            </div>
            <span className="text-[11px] sm:text-xs font-medium text-center leading-tight">{t.label}</span>
          </Link>
        ))}
      </div>
      </>)}

      {isAdmin && <PendingUsageCard compact />}

      {/* Traffic Monitor */}
      <Card className="bg-card border-border shadow-sm" data-testid="traffic-monitor-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <Activity className="w-4 h-4 text-sky-500" /> Trafik{telemetry?.traffic_iface ? <span className="text-muted-foreground font-mono text-[11px]">— {telemetry.traffic_iface}</span> : ""}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!selected || !telemetry ? (
            <div className="h-20 flex flex-col items-center justify-center gap-2.5 text-sm text-muted-foreground" data-testid="traffic-offline">
              <span className="flex items-center gap-2">
                {!selected ? "Pilih cabang/router terlebih dahulu" : (
                  <>
                    <RefreshCcw className="w-3.5 h-3.5 animate-spin text-sky-500" />
                    Router offline — mencoba menghubungi ulang otomatis tiap 5 detik...
                  </>
                )}
              </span>
              {selected && (
                <div className="w-44" data-testid="auto-refresh-progress">
                  <div className="h-1.5 rounded-full bg-accent overflow-hidden">
                    <div className="h-full bg-sky-500 auto-refresh-bar" />
                  </div>
                  <div className="text-[10px] font-mono text-muted-foreground mt-1.5 text-center">refresh otomatis tiap 5 detik</div>
                </div>
              )}
            </div>
          ) : (
            <>
              {(() => {
                const maxV = Math.max(1e3, ...traffic.flatMap((p) => [p.rx_bps, p.tx_bps]));
                const W = 300, H = 100;
                const pts = (key) => traffic.map((p, i) => `${traffic.length > 1 ? (i / (traffic.length - 1)) * W : W},${H - (p[key] / maxV) * (H - 6)}`).join(" ");
                const lastY = (key) => traffic.length ? (100 * (H - (traffic[traffic.length - 1][key] / maxV) * (H - 6))) / H : 100;
                const yRx = lastY("rx_bps"), yTx = lastY("tx_bps");
                const tooClose = Math.abs(yRx - yTx) < 14;
                const labelStyle = (y, up) => ({ top: `${Math.min(88, Math.max(0, y + (up ? -12 : 2)))}%` });
                const peakRx = Math.max(0, ...traffic.map((p) => p.rx_bps)), peakTx = Math.max(0, ...traffic.map((p) => p.tx_bps));
                return (
                  <div>
                    <div className="h-44 sm:h-56 relative rounded-xl overflow-hidden border border-border bg-gradient-to-b from-slate-900 to-slate-950" data-testid="traffic-bars">
                      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="absolute inset-0 w-full h-full" aria-hidden="true">
                        {[0.25, 0.5, 0.75].map((f) => <line key={f} x1="0" x2={W} y1={H * f} y2={H * f} stroke="#ffffff" strokeOpacity="0.07" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />)}
                      </svg>
                      {traffic.length > 1 ? (
                        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="relative w-full h-full" data-testid="traffic-line-chart">
                          <defs>
                            <linearGradient id="gradRx" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22c55e" stopOpacity="0.55" /><stop offset="100%" stopColor="#22c55e" stopOpacity="0.02" /></linearGradient>
                            <linearGradient id="gradTx" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#38bdf8" stopOpacity="0.4" /><stop offset="100%" stopColor="#38bdf8" stopOpacity="0.02" /></linearGradient>
                          </defs>
                          <polyline points={`0,${H} ${pts("rx_bps")} ${W},${H}`} fill="url(#gradRx)" stroke="none" />
                          <polyline points={`0,${H} ${pts("tx_bps")} ${W},${H}`} fill="url(#gradTx)" stroke="none" />
                          <polyline points={pts("rx_bps")} fill="none" stroke="#22c55e" strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                          <polyline points={pts("tx_bps")} fill="none" stroke="#38bdf8" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" strokeDasharray="5 3" vectorEffect="non-scaling-stroke" />
                        </svg>
                      ) : <div className="relative w-full h-full flex items-center justify-center text-xs text-slate-400" data-testid="traffic-sampling">Mengambil sampel traffic...</div>}
                      {traffic.length > 1 && (
                        <>
                          <span className="absolute right-2 text-[10px] font-mono font-bold text-emerald-400 bg-slate-900/80 border border-emerald-500/40 px-1.5 py-0.5 rounded leading-tight pointer-events-none transition-[top] duration-500" style={labelStyle(yRx, yRx >= yTx || !tooClose ? yRx > 50 : true)} data-testid="traffic-label-download">▼ DOWNLOAD {fmtBps(traffic[traffic.length - 1].rx_bps)}</span>
                          <span className="absolute right-2 text-[10px] font-mono font-bold text-sky-300 bg-slate-900/80 border border-sky-500/40 px-1.5 py-0.5 rounded leading-tight pointer-events-none transition-[top] duration-500" style={labelStyle(yTx, tooClose ? yTx < yRx : yTx > 50)} data-testid="traffic-label-upload">▲ UPLOAD {fmtBps(traffic[traffic.length - 1].tx_bps)}</span>
                          <span className="absolute left-2 top-1.5 text-[9px] font-mono text-slate-400 pointer-events-none" data-testid="traffic-peak">puncak ▼ {fmtBps(peakRx)} · ▲ {fmtBps(peakTx)}</span>
                          <span className="absolute left-2 bottom-1 text-[9px] font-mono text-slate-500 pointer-events-none">{traffic.length} sampel terakhir</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] font-mono text-muted-foreground">
                      <Link to="/hotspot-online" className="flex items-center gap-1.5 hover:text-foreground transition-colors" data-testid="traffic-hotspot-online"><Wifi className="w-3 h-3 text-blue-600" /> {telemetry.hotspot_active ?? 0} Hotspot online</Link>
                      <Link to="/ppp-online" className="flex items-center gap-1.5 hover:text-foreground transition-colors" data-testid="traffic-ppp-online"><Network className="w-3 h-3 text-rose-500" /> {telemetry.ppp_active ?? 0} PPP online</Link>
                    </div>
                  </div>
                );
              })()}
              {(() => {
                const used = Number(telemetry.quota_month_bytes || 0);
                const pct = Math.min(100, (used / FUP_LIMIT_BYTES) * 100);
                const bar = pct >= 90 ? "bg-rose-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";
                const now = new Date();
                const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
                const avgDay = now.getDate() > 0 ? used / now.getDate() : 0;
                const proj = avgDay * daysInMonth;
                const over = proj > FUP_LIMIT_BYTES;
                return (
                  <div className="space-y-1.5 text-[11px] font-mono" data-testid="router-info-grid">
                    <div className="rounded-lg bg-accent/50 px-2.5 py-1.5 space-y-0.5" data-testid="info-board">
                      <div className="truncate"><span className="text-muted-foreground">MikroTik</span> <b>{boardLabel(telemetry.board)}</b></div>
                      <div className="flex items-center justify-between gap-2 text-[10px]">
                        <span className="text-muted-foreground truncate">{telemetry.version ? `ROS ${String(telemetry.version).replace(/\s*\(.*\)\s*$/, "")}` : ""}</span>
                        <span className="shrink-0 flex items-center gap-2">
                          <span data-testid="info-cpu"><span className="text-muted-foreground">CPU</span> <b>{telemetry.cpu_load || 0}%</b></span>
                          <span data-testid="info-uptime"><span className="text-muted-foreground">up</span> <b data-testid="info-uptime-value">{fmtUptimeShort(telemetry.uptime)}</b></span>
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                      <div className="rounded-lg bg-accent/50 px-2 py-1.5 min-w-0" data-testid="info-ram"><div className="text-[9px] uppercase tracking-wider text-muted-foreground">RAM</div><div className="font-bold truncate">{fmtBytes(telemetry.total_memory - telemetry.free_memory)}</div><div className="text-[9px] text-muted-foreground truncate">dari {fmtBytes(telemetry.total_memory)}</div></div>
                      <div className="rounded-lg bg-accent/50 px-2 py-1.5 min-w-0" data-testid="info-quota-today"><div className="text-[9px] uppercase tracking-wider text-muted-foreground">Hari ini</div><div className="font-bold truncate">{fmtBytes(telemetry.quota_day_bytes || 0)}</div></div>
                      <div className="rounded-lg bg-accent/50 px-2 py-1.5 min-w-0" data-testid="info-quota"><div className="text-[9px] uppercase tracking-wider text-muted-foreground">Bulan ini</div><div className={`font-bold truncate ${pct >= 90 ? "text-rose-500" : ""}`}>{fmtBytes(used)}</div></div>
                    </div>
                    <div data-testid="quota-fup-bar">
                      <div className="h-1 rounded-full bg-muted overflow-hidden"><div className={`h-full rounded-full transition-all duration-500 ${bar}`} style={{ width: `${Math.max(pct, 1.5)}%` }} /></div>
                      <div className="flex flex-wrap justify-between gap-x-3 text-[9px] mt-0.5">
                        <span className={pct >= 90 ? "text-rose-500" : pct >= 70 ? "text-amber-600" : "text-muted-foreground"}>{pct.toFixed(1)}% dari FUP 4.000 GB</span>
                        <span className="text-muted-foreground" data-testid="quota-avg">rata² {fmtBytes(avgDay)}/hari</span>
                        <span className={over ? "text-rose-500 font-bold" : "text-muted-foreground"} data-testid="quota-projection">proyeksi {fmtBytes(proj)}{over ? " — lebihi FUP!" : ""}</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {false && telemetry.reset_logs?.length > 0 && (
                <div className="border-t border-border pt-2.5 space-y-1" data-testid="quota-reset-logs">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Riwayat Reset Counter (mati lampu / reboot)</div>
                  {telemetry.reset_logs.map((l, i) => (
                    <div key={i} className="flex items-center justify-between text-[11px] font-mono" data-testid={`quota-reset-log-${i}`}>
                      <span className="text-muted-foreground">{fmtDateTime(l.detected_at)}</span>
                      <span className="text-amber-600">+{fmtBytes(l.backed_up_bytes)} di-backup</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
